import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { useShowExplainer } from 'utils/hooks/useShowExplainer/useShowExplainer';
import SinglePoll from '../components/polls/SinglePoll';
import CharityExplainer from '../components/gamefication/charityExplainer/Explainer';
import PollsTitleBar from '../components/polls/PollsTitleBar';
import { getPolls as getPollsAction, retrieveCharities } from '../actions';
import ShowSpinner from '../components/spinner/ShowSpinner';
import FeedbackOnPolls from '../components/polls/FeedbackOnPolls';
import Panel from '../components/panel/Panel';
import { getQueries } from '../components/link/LinkHelper';

/**
 * Survey list page (/:slug/surveys).
 * Selecting a survey navigates to /:slug/surveys/:id, which the app renders as a
 * standalone survey page (src/containers/survey). The old popup flow that opened
 * surveys in a modal from here was removed in #2360.
 */
function Polls(props) {
  const { actions, slugId, history, location, charities, customizations } = props;

  // STATE
  const [polls, setPolls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showExplainer, setShowExplainer] = useShowExplainer();

  useEffect(() => {
    actions.retrieveCharities(slugId);
  }, [actions, slugId]);

  useEffect(() => {
    setIsLoading(true);
    actions.getPolls(slugId).then((res) => {
      setPolls(res);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, slugId]);

  function getPolls() {
    return polls.polls || [];
  }

  function getTrimmedPath() {
    let trimmedPath;
    if (location.pathname) {
      const array = location.pathname.split('/');
      trimmedPath = `/${array[0]}${array[1]}/${array[2]}`;
    }
    return trimmedPath;
  }

  function getPollClassModifier(index) {
    const wideModifier = getPolls().some((item) => !item.resource) ? 'c-card-poll--wide' : '';
    const firstItemOddModifier =
      getPolls().length % 2 && getPolls().some((item) => item.resource) && getPolls()[0].resource
        ? 'c-card-poll--floating'
        : '';
    const classModifier = `${wideModifier} ${index === 0 ? firstItemOddModifier : ''}`;
    return classModifier;
  }

  function pollClick(pollId) {
    history.push(`${getTrimmedPath()}/${pollId}${getQueries()}`);
  }

  return (
    <main className='l-section-main c-section-main' id='appContent' aria-live='polite'>
      <div className='l-container'>
        {polls.panel && <Panel history={props.history} panel={polls.panel} />}
        {getPolls().length > 0 && (
          <React.Fragment>
            <PollsTitleBar
              openExplainer={() => setShowExplainer(!showExplainer)}
              showExplainer={showExplainer}
              explainerVisible={charities.length > 0 && charities.filter((charity) => charity.isActive).length > 0}
            />
            {showExplainer && (
              <CharityExplainer showExplainer={showExplainer} setShowExplainer={setShowExplainer} slugId={slugId} />
            )}
            <div className='l-grid-polls u-a-children-fade-in'>
              <ShowSpinner showSpinner={isLoading} />
              {!isLoading &&
                !customizations.hidePollList &&
                getPolls().map((poll, index) => (
                  <SinglePoll
                    key={index}
                    name={poll.name}
                    questionCount={poll.questionCount}
                    isFinished={poll.isFinished}
                    completedQuestions={poll.completedQuestions}
                    coins={charities.filter((charity) => charity.isActive).length > 0 ? poll.coins : 0}
                    resource={poll.resource}
                    onClick={() => pollClick(poll._id)}
                    classModifier={getPollClassModifier(index)}
                  />
                ))}
              {/* Feedback */}
              <FeedbackOnPolls history={history} />
            </div>
          </React.Fragment>
        )}
      </div>
    </main>
  );
}

Polls.propTypes = {
  slugId: PropTypes.string.isRequired,
  actions: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  charities: state.charities.data,
  customizations: state.customizations,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      getPolls: getPollsAction,
      retrieveCharities,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Polls);
